import {
  Heading,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Box, Flex, Separator, useToken, IconButton } from '@chakra-ui/react';
import { CheckIcon, EditIcon, Undo2, X, XIcon } from 'lucide-react';
import {
  BlockArchetype,
  DashboardBlock,
  INSTRUCTIONS_NOT_EDITABLE_BLOCKS,
  UserBlock,
} from 'models/block';
import { UserBlockAction } from 'components/UserDashboard/overview/types';
import { useState } from 'react';
import BlockInstructionsEditor from 'components/UserDashboard/overview/BlockInstructionsEditor';

type Props = {
  patientInstructions: string;
  closeBlockDrawer: () => void;
  editableBlock: UserBlock | null;
  selectedBlock: DashboardBlock | null;
  handleUserBlockAction: (
    action: UserBlockAction,
    userBlockId?: string
  ) => void;
  onBackToBlockList: () => void;
  onPatientInstructionsChange: (value: string) => void;
  drawerSubtitle: string;
  handleUpdateUserBlock: () => void;
  drawerHeader: string;
};

const BlockDrawerHeader = ({
  patientInstructions,
  closeBlockDrawer,
  editableBlock,
  selectedBlock,
  handleUserBlockAction,
  onBackToBlockList,
  onPatientInstructionsChange,
  drawerSubtitle,
  handleUpdateUserBlock,
  drawerHeader,
}: Props) => {
  const [secondary200, bluegrey, seafoam, squash] = useToken('colors', [
    'secondary.200',
    'bluegrey',
    'seafoam',
    'squash',
  ]);
  const [editing, setEditing] = useState(false);
  const isContraindicatedBlock =
    editableBlock?.archetype === BlockArchetype.Contraindicated;
  const instructionsEditable =
    !!editableBlock &&
    !INSTRUCTIONS_NOT_EDITABLE_BLOCKS.includes(
      editableBlock.stable_string ?? ''
    ) &&
    !isContraindicatedBlock;

  const showBackToBlockList = !editableBlock && !!selectedBlock?.id;

  const headerActions: {
    title: string;
    onPress: () => void;
    primary: boolean;
    disabled?: boolean;
  }[] = editableBlock?.id
    ? [
        ...(editableBlock.archetype !== BlockArchetype.Goal
          ? [
              {
                title: 'Mark Complete',
                onPress: () => handleUserBlockAction('end', editableBlock.id),
                primary: true,
                disabled: !!editableBlock?.date_ended,
              },
            ]
          : []),
        {
          title: 'Archive',
          onPress: () => handleUserBlockAction('archive', editableBlock.id),
          primary: false,
          disabled: !!editableBlock?.date_archived,
        },
      ]
    : [];

  return (
    <VStack bg={'canvas'} position="sticky" top={0} zIndex={11}>
      <Flex
        alignItems="flex-start"
        flexDirection="row"
        justifyContent="space-between"
        p={6}
      >
        <VStack flex={1} pr={2}>
          <HStack alignItems="center">
            <Heading size="md">
              {editableBlock?.block_name || selectedBlock?.name || drawerHeader}
            </Heading>

            <HStack ml={'auto'} alignSelf={'flex-start'} flexShrink={0}>
              {headerActions?.map(({ disabled, onPress, primary, title }) => (
                <Pressable
                  disabled={disabled}
                  key={title}
                  py={1}
                  px={2}
                  onClick={onPress}
                  borderWidth={1}
                  mx={1}
                  bgColor={primary ? 'primary.50' : 'white'}
                  borderRadius={'full'}
                  borderColor={'cloud.200'}
                >
                  <Text
                    fontWeight={600}
                    color={'secondary.200'}
                    fontSize={'xs'}
                  >
                    {title}
                  </Text>
                </Pressable>
              ))}
            </HStack>
          </HStack>
          {!editing && (
            <HStack gap={2} alignItems={'center'} mt={1}>
              <Text color={'blueGray.600'} fontSize={'sm'}>
                {instructionsEditable
                  ? patientInstructions || drawerSubtitle
                  : drawerSubtitle}
              </Text>
              {instructionsEditable && (
                <Box
                  flexShrink={0}
                  cursor={'pointer'}
                  onClick={() => setEditing(true)}
                >
                  <EditIcon color={bluegrey} size={16} />
                </Box>
              )}
            </HStack>
          )}
        </VStack>

        <HStack gap={4} alignItems={'center'} flexShrink={0}>
          {showBackToBlockList && (
            <Pressable
              onClick={onBackToBlockList}
              aria-label="Back to block list"
            >
              <Undo2 color={secondary200} size={20} strokeWidth={2.5} />
            </Pressable>
          )}

          <Pressable onClick={closeBlockDrawer} aria-label="Close">
            <X color={secondary200} size={20} strokeWidth={2.5} />
          </Pressable>
        </HStack>
      </Flex>

      <Separator />
      {instructionsEditable && editing && (
        <VStack p={6}>
          <BlockInstructionsEditor
            onChange={onPatientInstructionsChange}
            instructions={patientInstructions}
          />
          <HStack mt={2} gap={2} w={'100%'} justifyContent={'flex-end'}>
            <IconButton
              borderColor={'cloud'}
              bgColor={'transparent'}
              onClick={handleUpdateUserBlock}
            >
              <CheckIcon color={seafoam} size={20} />
            </IconButton>
            <IconButton
              borderColor={'cloud'}
              bgColor={'transparent'}
              onClick={() => {
                onPatientInstructionsChange(editableBlock?.instructions ?? '');
                setEditing(false);
              }}
            >
              <XIcon color={squash} size={20} />
            </IconButton>
          </HStack>
        </VStack>
      )}
      <Separator />
    </VStack>
  );
};

export default BlockDrawerHeader;
